/**
 * Client-side image helpers for the signup flow.
 *
 * The signup steps stage images as data URLs before the account is created, so
 * every picked photo is sent as one JSON body. Uncompressed phone photos are
 * 2-6MB each (and 33% larger once base64-encoded), which used to exhaust the
 * per-tab sessionStorage quota and silently drop the last image. Downscaling to
 * a document-sized JPEG keeps the whole flow well under the quota.
 *
 * Mirrors the RN `uriToDataUri` behaviour (resize + JPEG re-encode) so web and
 * mobile send comparable payloads.
 */

interface ReadImageOptions {
  /** Longest edge of the output, in px. Defaults to 1024. */
  maxEdge?: number;
  /** JPEG quality between 0 and 1. Defaults to 0.85. */
  quality?: number;
}

/** Reads a picked file as a data URL, re-encoded as a bounded-size JPEG. */
export const readImageFileAsDataUrl = async (
  file: File,
  options: ReadImageOptions = {},
): Promise<string> => {
  const { maxEdge = 1024, quality = 0.85 } = options;
  const rawDataUrl = await readAsDataUrl(file);
  if (!rawDataUrl) return "";

  // Formats a browser cannot decode (HEIC on non-Safari, corrupt files) or a
  // downscale that is bigger than the source: keep the original untouched.
  try {
    const downscaled = await drawToJpegDataUrl(rawDataUrl, maxEdge, quality);
    return downscaled || rawDataUrl;
  } catch (err) {
    console.warn(`[readImageFileAsDataUrl] downscale skipped: ${err}`);
    return rawDataUrl;
  }
};

const readAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("read failed"));
    reader.readAsDataURL(file);
  });

const drawToJpegDataUrl = (
  dataUrl: string,
  maxEdge: number,
  quality: number,
): Promise<string> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const longestEdge = Math.max(image.naturalWidth, image.naturalHeight);
      if (!longestEdge) return reject(new Error("empty image"));
      const scale = Math.min(1, maxEdge / longestEdge);
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
      const context = canvas.getContext("2d");
      if (!context) return reject(new Error("2d context unavailable"));
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    image.onerror = () => reject(new Error("image decode failed"));
    image.src = dataUrl;
  });
